import React, { useState } from 'react';
import './App.css';
import translations from './translations';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function App() {
  const [chars, setChars] = useState('');
  const [words, setWords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('tr');
  const [selectedWord, setSelectedWord] = useState(null);
  const [meaning, setMeaning] = useState(null);
  const [meaningLoading, setMeaningLoading] = useState(false);
  const [meaningError, setMeaningError] = useState(null);

  const t = translations[language];

  const handleSearch = async () => {
    if (!chars.trim()) {
      setError(t.enterLetters);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/api/search?chars=${encodeURIComponent(chars)}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t.fetchError);
      }

      if (!data.grouped) {
        throw new Error(t.invalidResponse);
      }

      setWords(data);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message);
      setWords(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleWordClick = async (word) => {
    setSelectedWord(word);
    setMeaning(null);
    setMeaningError(null);
    setMeaningLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/word/${encodeURIComponent(word)}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.meaningError);
      }
      setMeaning(data);
    } catch (err) {
      console.error('Meaning error:', err);
      setMeaningError(err.message);
    } finally {
      setMeaningLoading(false);
    }
  };

  const closeMeaningPanel = () => {
    setSelectedWord(null);
    setMeaning(null);
    setMeaningError(null);
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="language-switcher">
          <label htmlFor="language-select">{t.language}</label>
          <select
            id="language-select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="language-select"
          >
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </select>
        </div>
        <h1>{t.title}</h1>
      </header>
      
      <main>
        <div className="search-container">
          <input
            type="text"
            value={chars}
            onChange={(e) => setChars(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t.placeholder}
            className="search-input"
          />
          <button 
            onClick={handleSearch}
            disabled={loading}
            className="search-button"
          >
            {loading ? t.searching : t.search}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {words?.grouped && Object.keys(words.grouped).length > 0 && (
          <div className="results-container">
            <div className="total-count">
              {t.foundWords(words.total)}
            </div>
            <div className="word-groups-grid">
              {Object.entries(words.grouped)
                .sort(([lengthA], [lengthB]) => parseInt(lengthA) - parseInt(lengthB))
                .map(([length, wordList]) => (
                  <div key={length} className="word-group">
                    <h2>{t.lettersLabel(length, wordList.length)}</h2>
                    <div className="word-list">
                      {wordList.map((word, index) => (
                        <button
                          key={index}
                          type="button"
                          className="word-item"
                          onClick={() => handleWordClick(word)}
                        >
                          {word}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {!loading && !error && (!words?.grouped || Object.keys(words?.grouped || {}).length === 0) && (
          <div className="no-results">
            {t.noResults}
          </div>
        )}

        {selectedWord && (
          <div className="meaning-panel-backdrop" onClick={closeMeaningPanel}>
            <aside
              className="meaning-panel"
              onClick={(event) => event.stopPropagation()}
              aria-label={t.meaningTitle}
            >
              <div className="meaning-panel-header">
                <h2>{selectedWord}</h2>
                <button
                  type="button"
                  className="meaning-panel-close"
                  onClick={closeMeaningPanel}
                  aria-label={t.close}
                >
                  ×
                </button>
              </div>
              {meaningLoading && <p className="meaning-status">{t.meaningLoading}</p>}
              {meaningError && <p className="meaning-error">{meaningError}</p>}
              {meaning && (
                meaning.meanings.length > 0 ? (
                  <ol className="meaning-list">
                    {meaning.meanings.map((item, index) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ))}
                  </ol>
                ) : (
                  <p className="meaning-status">{t.noMeaning}</p>
                )
              )}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
